import merge from "lodash/merge";

import createHash from "@framework/relay/server/network/helpers/create-hash";
import getQueryText from "@framework/relay/server/network/helpers/get-query-text";
import ServerMiddleware from "@framework/relay/server/network/server-middleware";
import { log } from "@voxmedia/duet-utils/logging";

/**
 * This structure describes a GraphQL operation with a persistedQuery extension.
 *
 * This type is based on the spec provided in the Apollo Server documentation:
 * https://www.apollographql.com/docs/apollo-server/performance/apq/#command-line-testing
 */
interface PersistedQueryExtension {
  version: 1;

  /**
   * sha256Hash is the sha256Hash of the underlying query.
   */
  sha256Hash: string;
}
interface PersistedQueryParams {
  // queryId is the sha256Hash of the underlying query.
  queryId: string;
  // name of the GQL operation
  operationName: string;
  // variables for the GQL operation
  variables: Record<string, any>;
}

/**
 * createURL will merge in the GraphQL Request as query parameters.
 *
 * @param fetchURL the URL to add the query parameters to
 * @param paramData the parameters for a GraphQL request
 * @returns the URL that's compatible with an HTTP GET
 */
function createGETURL(
  fetchURL: string,
  paramData: PersistedQueryParams,
): string {
  const url = new URL(fetchURL);

  for (const [key, value] of Object.entries(paramData)) {
    const param = typeof value === "string" ? value : JSON.stringify(value);
    url.searchParams.append(key, param);
  }

  return url.href;
}

const memoizedCreateHash = (() => {
  const results: Record<string, string> = {};
  return async (text: string) => {
    if (!(text in results)) {
      results[text] = await createHash({ text });
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return results[text]!;
  };
})();

/**
 * automaticPersistedQueryMiddleware implements support for Automatic Persisted
 * Queries (or APQ).
 *
 * @see https://www.apollographql.com/docs/react/api/link/persisted-queries/#protocol
 *
 * @param persisted the persisted query map
 * @returns a middleware for the relay network layer
 */
const automaticPersistedQueryMiddleware = (
  persisted: Record<string, string>,
): ServerMiddleware => {
  return (next) => async (ctx) => {
    if (ctx.request.operationKind !== "query" || !ctx.request.id) {
      return next(ctx);
    }

    if (!(ctx.request.id in persisted)) {
      // NOTE: we only support APQ for persisted queries from Relay at this time.
      log.debug(
        `---------! WARNING: Request for ${ctx.operation.operationName} not found in persisted data - are you using the correct query fetcher?`,
      );
      return next(ctx);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const hash = await memoizedCreateHash(persisted[ctx.request.id]!);

    // Add SHA-256 hash to log context for structured logging
    ctx.logContext = merge({}, ctx.logContext, {
      query: {
        persistedQuery: {
          sha256Hash: hash,
        },
      },
    });

    // Grab the original values from the context before we modify them.
    const {
      url: originalURL,
      fetchOpts: { method: originalMethod },
    } = ctx;

    // Configure this as a GET.
    ctx.fetchOpts.method = "GET";

    /**
     * The persisted query parameters that will be sent with the request. WPGQL
     * will use the SHA256 hash queryId to identify the persisted query document
     * associated with the query.
     */
    const persistedQueryParams: PersistedQueryParams = {
      queryId: hash,
      operationName: ctx.operation.operationName,
      variables: ctx.operation.variables,
    };

    // Add the parameters to this GET request.
    ctx.url = createGETURL(originalURL, persistedQueryParams);

    // Attempt the request (assuming that it supports APQ).
    const res = await next(ctx);

    if ("errors" in res && res.errors && res.errors[0]) {
      if (res.errors[0].message === "PersistedQueryNotFound") {
        // This query was not found on the server! Revert the changes made to
        // the body so we can send the query along side the persisted query
        // details so the server can cache it.
        ctx.url = originalURL;
        ctx.fetchOpts.method = originalMethod;

        /**
         * Data sent in the persisted query extension is used by WPGQL to
         * associate the query with an ID, used as the alias in the GraphQL
         * documents custom post type, and store the peristed query.
         */
        const persistedQuery: PersistedQueryExtension = {
          version: 1,
          sha256Hash: hash,
        };

        // Add the extension to the operation.
        ctx.operation.extensions = {
          ...ctx.operation.extensions,
          persistedQuery,
        };
        ctx.operation.query = getQueryText(persisted, ctx.request);

        return next(ctx);
      }
    }

    return res;
  };
};

export default automaticPersistedQueryMiddleware;
